import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Alert,
  TextField,
  CircularProgress,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Autocomplete,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Send as SendIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  AutoFixHigh as RegenIcon,
  AccountCircle as CustomerIcon,
  OpenInNew as OpenInNewIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  Speed as MeterIcon,
  Build as InterventionIcon,
  NoteAdd as NoteIcon,
  Warning as WarningIcon,
  LocalShipping as DeliveryIcon,
  EventNote as PlanIcon,
  ContactPhone as ContactIcon,
  FlashOn as QuickActionIcon,
  Refresh as ReanalyzeIcon,
  AttachFile as AttachFileIcon,
  Description as AccountStatementIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  PersonAdd as PersonAddIcon,
  InsertDriveFile as FileIcon,
  SaveAlt as SaveAltIcon,
} from '@mui/icons-material';
import AiCategoryBadge from './AiCategoryBadge';
import api, { aiMailApi, customersApi, emailsApi, customerDocumentsApi } from '../../services/api';

// Separates email body from operator notes using the AI delimiter
// Also handles legacy formats: [NOTA: ...], [Nota per l'operatore: ...], *Nota: ...
const NOTES_SEPARATOR = '---NOTA PER OPERATORE---';
function splitDraftAndNotes(draft) {
  if (!draft) return { body: '', notes: '' };
  // Primary: explicit separator
  const sepIdx = draft.indexOf(NOTES_SEPARATOR);
  if (sepIdx !== -1) {
    return {
      body: draft.substring(0, sepIdx).trimEnd(),
      notes: draft.substring(sepIdx + NOTES_SEPARATOR.length).trim(),
    };
  }
  // Fallback: detect common annotation patterns at end of draft
  // Match lines like [NOTA: ...], [Nota per l'operatore: ...], *Nota: ..., **Nota: ..., NB: ...
  const fallbackPattern = /\n\s*(\[(?:NOTA|Nota)[^\]]*\].*|(?:\*{1,2})?(?:Nota|NOTA|NB|N\.B\.)[\s:].*)$/s;
  const match = draft.match(fallbackPattern);
  if (match) {
    return {
      body: draft.substring(0, match.index).trimEnd(),
      notes: match[0].trim(),
    };
  }
  return { body: draft, notes: '' };
}

// Helper: scarica file autenticato via axios e apre in nuova scheda
const openAuthenticatedUrl = async (apiPath) => {
  try {
    const res = await api.get(apiPath, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch (err) {
    console.error('Errore apertura file:', err);
    alert('Errore nel caricamento del file');
  }
};

export default function AiReplyReviewDialog({ open, onClose, action, onApprove, onReject, onEditDraft, onRegenerate, onReanalyze }) {
  const [editMode, setEditMode] = useState(false);
  const [editedDraft, setEditedDraft] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [operatorNotes, setOperatorNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [showFullBody, setShowFullBody] = useState(false);
  const [fetchedCrmData, setFetchedCrmData] = useState(null);
  const [loadingCrm, setLoadingCrm] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const prevActionIdRef = useRef(null);
  const BODY_PREVIEW_LENGTH = 600;

  // Customer association state
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [assigningCustomer, setAssigningCustomer] = useState(false);
  const [localCustomer, setLocalCustomer] = useState(null);

  // Account statement dialog state
  const [accountStatementOpen, setAccountStatementOpen] = useState(false);
  const [accountStatementInvoices, setAccountStatementInvoices] = useState([]);
  const [accountStatementLoading, setAccountStatementLoading] = useState(false);
  const [selectedFromInvoiceId, setSelectedFromInvoiceId] = useState(null);

  // File upload ref
  const fileInputRef = useRef(null);

  // Reply-to and CC state
  const [replyToAddress, setReplyToAddress] = useState('');
  const [ccAddresses, setCcAddresses] = useState('');

  // Save attachment to customer state
  const [saveAttDialog, setSaveAttDialog] = useState({ open: false, att: null });
  const [saveAttDescription, setSaveAttDescription] = useState('');
  const [savingAtt, setSavingAtt] = useState(false);

  useEffect(() => {
    if (action) {
      const isNewAction = action.id !== prevActionIdRef.current;
      prevActionIdRef.current = action.id;

      if (isNewAction) {
        // Reset everything only when opening a different action
        const { body, notes } = splitDraftAndNotes(action.reply_draft || '');
        setEditedDraft(body);
        setOperatorNotes(notes);
        setEditedSubject(action.reply_subject || '');
        setEditMode(false);
        setAttachments([]);
        setLocalCustomer(null);
        setCustomerSearchText('');
        setCustomerOptions([]);
        setReplyToAddress(action.reply_to_address || action.email?.from_address || '');
        setCcAddresses('');
      }
    }
  }, [action]);

  // Fetch CRM context from API when dialog opens with a customer
  useEffect(() => {
    // Try to find customer_id from email or from action_params
    let customerId = action?.email?.customer_id;
    if (!customerId && action?.action_params) {
      try {
        const params = typeof action.action_params === 'string'
          ? JSON.parse(action.action_params)
          : action.action_params;
        customerId = params?.customer_id;
      } catch { /* ignore */ }
    }
    if (!open || !customerId) {
      if (!open) setFetchedCrmData(null);
      return;
    }

    // Always fetch from API to get invoices_data for attachment buttons
    setLoadingCrm(true);
    aiMailApi.getCrmContext(customerId)
      .then(res => {
        if (res.data?.crm_context) {
          setFetchedCrmData({
            crm_context: res.data.crm_context,
            crm_actions: [],
            invoices_data: res.data.invoices_data || [],
            customer_id: res.data.customer_id || customerId,
          });
        }
      })
      .catch(err => console.warn('CRM context fetch failed:', err.message))
      .finally(() => setLoadingCrm(false));
  }, [open, action?.email?.customer_id, action?.action_params]);

  // Parse CRM context from action_params OR fetched data
  const { crmContext, crmActions, invoicesData, crmCustomerId } = useMemo(() => {
    let rawContext = '';
    let rawActions = [];
    let rawInvoices = [];
    let rawCustomerId = null;

    try {
      if (action?.action_params) {
        const params = typeof action.action_params === 'string'
          ? JSON.parse(action.action_params)
          : action.action_params;
        if (params?.crm_context) rawContext = params.crm_context;
        if (params?.crm_actions) rawActions = params.crm_actions;
        if (params?.invoices_data) rawInvoices = params.invoices_data;
        if (params?.customer_id) rawCustomerId = params.customer_id;
      }
    } catch { /* ignore */ }

    // Fallback to fetched data
    if (!rawContext && fetchedCrmData) {
      rawContext = fetchedCrmData.crm_context || '';
      if (!rawActions.length) rawActions = fetchedCrmData.crm_actions || [];
    }

    // invoices_data from fetched CRM (override action_params if available, since fetch is more up-to-date)
    if (fetchedCrmData?.invoices_data && fetchedCrmData.invoices_data.length > 0) {
      rawInvoices = fetchedCrmData.invoices_data;
    }
    if (fetchedCrmData?.customer_id) {
      rawCustomerId = fetchedCrmData.customer_id;
    }

    // Normalize crm_actions: can be strings or objects
    const actionLabels = {
      verifica_scaduto: 'Verificare fatture scadute',
      registra_pagamento: 'Registrare pagamento',
      crea_fattura: 'Creare fattura',
      aggiorna_lettura: 'Aggiornare lettura contatore',
      crea_intervento: 'Creare intervento tecnico',
      aggiungi_nota: 'Aggiungere nota cliente',
      invia_sollecito: 'Inviare sollecito',
      verifica_piano_rate: 'Verificare piano rate',
      aggiorna_anagrafica: 'Aggiornare anagrafica',
      schedula_consegna: 'Schedulare consegna',
    };
    const normalized = rawActions.map(a => {
      if (typeof a === 'string') {
        return { action: a, label: actionLabels[a] || a, reason: '', priority: 'medium' };
      }
      return { action: a.action || '', label: a.label || actionLabels[a.action] || a.action || '', reason: a.reason || '', priority: a.priority || 'medium' };
    });

    return { crmContext: rawContext, crmActions: normalized, invoicesData: rawInvoices, crmCustomerId: rawCustomerId };
  }, [action?.action_params, fetchedCrmData]);

  // Parse CRM context into sections for display
  const crmSections = useMemo(() => {
    if (!crmContext) return [];
    const sections = [];
    const lines = crmContext.split('\n');
    let currentSection = null;
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      // Section headers: all caps with colon or parentheses
      if (/^[A-ZÀÈÉÌÒÙ\s]+[\s(:]/.test(trimmed) && !trimmed.startsWith('-') && !trimmed.startsWith('⚠')) {
        currentSection = { title: trimmed.replace(/:$/, ''), items: [] };
        sections.push(currentSection);
      } else if (trimmed.startsWith('⚠')) {
        sections.push({ title: trimmed, items: [], isWarning: true });
      } else if (currentSection) {
        currentSection.items.push(trimmed.replace(/^\s*-\s*/, '').replace(/^\s{2}/, ''));
      }
    }
    return sections;
  }, [crmContext]);

  // Extract CRM keywords for highlighting
  const crmKeywords = useMemo(() => {
    if (!crmContext) return [];
    const keywords = new Set();
    const amounts = crmContext.match(/€\s*[\d.,]+|[\d.,]+\s*€/g);
    if (amounts) amounts.forEach(a => keywords.add(a.trim()));
    const docNums = crmContext.match(/(?:N\.|FAT|n\.)[\s/]*[\d/]+/gi);
    if (docNums) docNums.forEach(d => keywords.add(d.trim()));
    const dates = crmContext.match(/\d{2}\/\d{2}\/\d{4}/g);
    if (dates) dates.forEach(d => keywords.add(d));
    const nameMatch = crmContext.match(/Cliente:\s*(.+?)(?:\n|$)/);
    if (nameMatch) keywords.add(nameMatch[1].trim());
    const cfMatch = crmContext.match(/(?:CF|P\.IVA|Cod\.Fisc)[\s:]*([A-Z0-9]+)/gi);
    if (cfMatch) cfMatch.forEach(c => {
      const val = c.split(/[\s:]+/).pop();
      if (val && val.length > 5) keywords.add(val);
    });
    return [...keywords].filter(k => k.length >= 3);
  }, [crmContext]);

  if (!action) return null;

  const email = action.email || {};
  const customer = email.customer || null;

  // Highlight CRM-sourced text in the reply draft
  const highlightCrmText = (text) => {
    if (!text || crmKeywords.length === 0) return text;
    const escaped = crmKeywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
    const parts = text.split(pattern);
    return parts.map((part, i) => {
      if (pattern.test(part)) {
        pattern.lastIndex = 0;
        return (
          <span key={i} style={{
            backgroundColor: '#fff3cd',
            borderBottom: '2px solid #ffc107',
            padding: '0 2px',
            borderRadius: 2,
          }}>{part}</span>
        );
      }
      return part;
    });
  };

  // Get email body text
  const emailBody = email.body_text
    || (email.body_html ? email.body_html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '');

  // Attachment helpers
  const toggleInvoiceAttachment = (invoice) => {
    setAttachments(prev => {
      const exists = prev.find(a => a.type === 'invoice' && a.id === invoice.id);
      if (exists) return prev.filter(a => !(a.type === 'invoice' && a.id === invoice.id));
      return [...prev, {
        type: 'invoice',
        id: invoice.id,
        label: `Fattura N.${invoice.nuf || invoice.id}`,
        filename: `fattura_${invoice.nuf || invoice.id}.pdf`,
      }];
    });
  };

  const toggleAccountStatement = async () => {
    const custId = crmCustomerId || customer?.id || email.customer_id;
    // If already attached, just remove it
    const exists = attachments.find(a => a.type === 'account_statement');
    if (exists) {
      setAttachments(prev => prev.filter(a => a.type !== 'account_statement'));
      return;
    }
    // Open sub-dialog to select starting invoice
    setAccountStatementOpen(true);
    setSelectedFromInvoiceId(null);
    setAccountStatementLoading(true);
    try {
      const res = await customersApi.getInvoices(custId);
      const invoices = res.data?.data || res.data?.invoices || [];
      setAccountStatementInvoices(Array.isArray(invoices) ? invoices : []);
    } catch (err) {
      console.warn('Failed to load invoices for account statement:', err.message);
      setAccountStatementInvoices([]);
    } finally {
      setAccountStatementLoading(false);
    }
  };

  const confirmAccountStatement = () => {
    const custId = crmCustomerId || customer?.id || email.customer_id;
    setAttachments(prev => {
      const filtered = prev.filter(a => a.type !== 'account_statement');
      return [...filtered, {
        type: 'account_statement',
        id: custId,
        fromInvoiceId: selectedFromInvoiceId || undefined,
        label: selectedFromInvoiceId
          ? `Estratto Conto (da fatt. ${accountStatementInvoices.find(i => i.id === selectedFromInvoiceId)?.nuf || '?'})`
          : 'Estratto Conto (completo)',
        filename: 'estratto_conto.pdf',
      }];
    });
    setAccountStatementOpen(false);
  };

  // Customer search handler
  const handleCustomerSearch = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setCustomerOptions([]);
      return;
    }
    setCustomerSearching(true);
    try {
      const res = await customersApi.getAll({ search: searchText, limit: 15, page: 1 });
      const customers = res.data?.customers || res.data?.rows || res.data || [];
      setCustomerOptions(Array.isArray(customers) ? customers : []);
    } catch (err) {
      console.warn('Customer search failed:', err.message);
    } finally {
      setCustomerSearching(false);
    }
  };

  // Assign customer to email
  const handleAssignCustomer = async (selectedCustomer) => {
    if (!selectedCustomer || !email.id) return;
    setAssigningCustomer(true);
    try {
      await emailsApi.assignCustomer(email.id, selectedCustomer.id);
      // Update local state so CRM panel reloads
      setLocalCustomer(selectedCustomer);
      email.customer_id = selectedCustomer.id;
      email.customer = selectedCustomer;
      // Force CRM data reload
      setFetchedCrmData(null);
    } catch (err) {
      console.error('Failed to assign customer:', err);
    } finally {
      setAssigningCustomer(false);
    }
  };

  // File upload handler
  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1]; // Remove data:...;base64, prefix
        setAttachments(prev => [...prev, {
          type: 'file',
          content: base64,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          label: file.name,
          size: file.size,
        }]);
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await onEditDraft(action.id, {
        reply_draft: editedDraft,
        reply_subject: editedSubject
      });
      setEditMode(false);
    } catch (err) {
      console.error('Error saving draft:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    setSending(true);
    try {
      // Save edits first if in edit mode
      if (editMode) {
        await onEditDraft(action.id, {
          reply_draft: editedDraft,
          reply_subject: editedSubject
        });
      }
      await onApprove(action.id, attachments.length > 0 ? attachments : undefined, replyToAddress || undefined, ccAddresses || undefined);
      onClose();
    } catch (err) {
      console.error('Error approving:', err);
    } finally {
      setSending(false);
    }
  };

  const handleReject = async () => {
    try {
      await onReject(action.id);
      onClose();
    } catch (err) {
      console.error('Error rejecting:', err);
    }
  };

  const handleRegenerate = async () => {
    if (!regenPrompt.trim() || !onRegenerate) return;
    setRegenerating(true);
    try {
      const result = await onRegenerate(action.id, regenPrompt.trim());
      if (result?.reply_draft) {
        const { body, notes } = splitDraftAndNotes(result.reply_draft);
        setEditedDraft(body);
        setOperatorNotes(notes);
      }
      setRegenPrompt('');
    } catch (err) {
      console.error('Error regenerating:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const handleReanalyze = async () => {
    if (!onReanalyze) return;
    setReanalyzing(true);
    try {
      const result = await onReanalyze(action.id);
      if (result?.action) {
        // Update draft/subject from re-analyzed action (attachments preserved)
        const { body, notes } = splitDraftAndNotes(result.action.reply_draft || '');
        setEditedDraft(body);
        setOperatorNotes(notes);
        setEditedSubject(result.action.reply_subject || '');
        setEditMode(false);
        setFetchedCrmData(null); // Reset so it re-reads from action_params
      }
    } catch (err) {
      console.error('Error reanalyzing:', err);
    } finally {
      setReanalyzing(false);
    }
  };

  // Icon map for CRM actions
  const crmActionIcons = {
    verifica_scaduto: <WarningIcon color="warning" />,
    registra_pagamento: <PaymentIcon color="success" />,
    crea_fattura: <InvoiceIcon color="primary" />,
    aggiorna_lettura: <MeterIcon color="info" />,
    crea_intervento: <InterventionIcon color="secondary" />,
    aggiungi_nota: <NoteIcon />,
    invia_sollecito: <WarningIcon color="error" />,
    verifica_piano_rate: <PlanIcon color="info" />,
    aggiorna_anagrafica: <ContactIcon color="primary" />,
    schedula_consegna: <DeliveryIcon color="success" />,
  };

  // CRM section icons
  const sectionIcon = (title) => {
    const t = title.toUpperCase();
    if (t.includes('FATTUR')) return <InvoiceIcon fontSize="small" />;
    if (t.includes('PAGAMENT')) return <PaymentIcon fontSize="small" />;
    if (t.includes('CONTAT')) return <MeterIcon fontSize="small" />;
    if (t.includes('INTERVENT')) return <InterventionIcon fontSize="small" />;
    if (t.includes('NOTE')) return <NoteIcon fontSize="small" />;
    if (t.includes('PIAN') || t.includes('RATE')) return <PlanIcon fontSize="small" />;
    if (t.includes('DATI') || t.includes('CLIENTE')) return <PersonIcon fontSize="small" />;
    return null;
  };

  const hasCrmPanel = crmContext || crmActions.length > 0 || loadingCrm;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <EmailIcon color="primary" />
        Review Risposta AI
        {customer && (
          <Chip
            icon={<CustomerIcon />}
            label={customer.ragione_sociale || `${customer.nome || ''} ${customer.cognome || ''}`.trim() || 'Cliente'}
            color="info"
            variant="outlined"
            size="small"
            clickable
            deleteIcon={<OpenInNewIcon fontSize="small" />}
            onDelete={() => window.open(`/crm/clienti/${customer.id}`, '_blank')}
            onClick={() => window.open(`/crm/clienti/${customer.id}`, '_blank')}
            sx={{ fontWeight: 600 }}
          />
        )}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Close button */}
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          {/* Re-analyze button */}
          <Tooltip title="Rianalizza email (aggiorna classificazione e dati CRM)">
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={reanalyzing ? <CircularProgress size={14} /> : <ReanalyzeIcon />}
              onClick={handleReanalyze}
              disabled={reanalyzing}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {reanalyzing ? 'Rianalisi...' : 'Rianalizza'}
            </Button>
          </Tooltip>
          {action.ai_category && (
            <AiCategoryBadge category={action.ai_category} confidence={action.ai_confidence} size="small" />
          )}
          <Chip
            label={`${((action.ai_confidence || 0) * 100).toFixed(0)}%`}
            size="small"
            color={action.ai_confidence >= 0.8 ? 'success' : action.ai_confidence >= 0.5 ? 'warning' : 'default'}
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: { xs: 'auto', md: '70vh' }, flexDirection: { xs: 'column', md: 'row' } }}>

          {/* ========== LEFT COLUMN: Original Email ========== */}
          <Box sx={{
            flex: '0 0 30%', minWidth: 0, p: 2, borderRight: { md: '1px solid' }, borderColor: { md: 'divider' },
            overflow: 'auto', bgcolor: 'grey.50',
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700}>Email ricevuta</Typography>
            </Stack>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Da:</strong> {email.from_name ? `${email.from_name} <${email.from_address}>` : email.from_address}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Oggetto:</strong> {email.subject}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Data:</strong> {email.date_sent ? new Date(email.date_sent).toLocaleString('it-IT') : '-'}
              </Typography>
            </Box>

            {/* Customer association */}
            {(customer || localCustomer) ? (
              <Chip
                icon={<CustomerIcon />}
                label={(localCustomer || customer)?.ragione_sociale || `${(localCustomer || customer)?.nome || ''} ${(localCustomer || customer)?.cognome || ''}`.trim() || 'Cliente'}
                color="info"
                variant="outlined"
                size="small"
                clickable
                deleteIcon={<OpenInNewIcon fontSize="small" />}
                onDelete={() => window.open(`/crm/clienti/${(localCustomer || customer).id}`, '_blank')}
                onClick={() => window.open(`/crm/clienti/${(localCustomer || customer).id}`, '_blank')}
                sx={{ fontWeight: 600, mb: 1.5 }}
              />
            ) : (
              <Autocomplete
                size="small"
                options={customerOptions}
                getOptionLabel={(opt) => opt.ragione_sociale || `${opt.nome || ''} ${opt.cognome || ''}`.trim() || `ID ${opt.id}`}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {opt.ragione_sociale || `${opt.nome || ''} ${opt.cognome || ''}`.trim()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.cod_cli ? `Cod: ${opt.cod_cli}` : ''} {opt.email ? `• ${opt.email}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                loading={customerSearching}
                onInputChange={(_, value) => {
                  setCustomerSearchText(value);
                  handleCustomerSearch(value);
                }}
                onChange={(_, value) => value && handleAssignCustomer(value)}
                noOptionsText={customerSearchText.length < 2 ? 'Digita almeno 2 caratteri...' : 'Nessun cliente trovato'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associa cliente"
                    placeholder="Cerca per nome, codice, email..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: assigningCustomer
                        ? <CircularProgress size={16} sx={{ mr: 1 }} />
                        : <PersonAddIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
                    }}
                  />
                )}
                sx={{ mb: 1.5 }}
                disabled={assigningCustomer}
              />
            )}

            {action.ai_category && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }} icon={false}>
                Classificazione: <strong>{action.ai_category}</strong> — Confidence: <strong>{((action.ai_confidence || 0) * 100).toFixed(0)}%</strong>
              </Alert>
            )}

            <Divider sx={{ mb: 1.5 }} />

            {emailBody && (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.primary', fontSize: '0.82rem' }}
              >
                {showFullBody || emailBody.length <= BODY_PREVIEW_LENGTH
                  ? emailBody
                  : emailBody.substring(0, BODY_PREVIEW_LENGTH) + '...'}
              </Typography>
            )}
            {emailBody && emailBody.length > BODY_PREVIEW_LENGTH && (
              <Button
                size="small"
                onClick={() => setShowFullBody(!showFullBody)}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                {showFullBody ? 'Mostra meno' : 'Mostra tutto'}
              </Button>
            )}

            {/* Email received attachments */}
            {email.attachments && email.attachments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block' }}>
                  📎 Allegati email ({email.attachments.length})
                </Typography>
                <Stack spacing={0.5}>
                  {(email.attachments || []).filter(a => !a.is_inline).map((att) => (
                    <Box key={att.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        icon={<FileIcon fontSize="small" />}
                        label={`${att.filename || 'Allegato'}${(att.size_bytes || att.size) ? ` (${((att.size_bytes || att.size) / 1024).toFixed(0)} KB)` : ''}`}
                        size="small"
                        variant="outlined"
                        clickable
                        onClick={() => openAuthenticatedUrl(`/emails/${email.id}/attachments/${att.id}`)}
                        deleteIcon={<DownloadIcon fontSize="small" />}
                        onDelete={() => openAuthenticatedUrl(`/emails/${email.id}/attachments/${att.id}`)}
                        sx={{ fontSize: '0.72rem', justifyContent: 'flex-start', flex: 1 }}
                      />
                      {email.customer_id && (
                        <Tooltip title="Salva in anagrafica cliente">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              const emailDate = email.date ? new Date(email.date).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
                              setSaveAttDescription(`Allegato email del ${emailDate}`);
                              setSaveAttDialog({ open: true, att });
                            }}
                          >
                            <SaveAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Dialog conferma salvataggio allegato in anagrafica */}
            <Dialog open={saveAttDialog.open} onClose={() => setSaveAttDialog({ open: false, att: null })} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontSize: '0.95rem' }}>Salva in anagrafica cliente</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Salvare <strong>{saveAttDialog.att?.filename}</strong> nei documenti del cliente?
                </Typography>
                <TextField
                  fullWidth
                  label="Descrizione"
                  value={saveAttDescription}
                  onChange={(e) => setSaveAttDescription(e.target.value)}
                  size="small"
                  autoFocus
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSaveAttDialog({ open: false, att: null })} disabled={savingAtt}>Annulla</Button>
                <Button
                  variant="contained"
                  disabled={savingAtt}
                  startIcon={savingAtt ? <CircularProgress size={16} /> : <SaveAltIcon />}
                  onClick={async () => {
                    setSavingAtt(true);
                    try {
                      await customerDocumentsApi.saveFromEmail(
                        email.customer_id,
                        email.id,
                        saveAttDialog.att.id,
                        saveAttDescription
                      );
                      alert('Documento salvato in anagrafica cliente');
                      setSaveAttDialog({ open: false, att: null });
                    } catch (err) {
                      console.error('Errore salvataggio:', err);
                      alert('Errore nel salvataggio: ' + (err.response?.data?.message || err.message));
                    } finally {
                      setSavingAtt(false);
                    }
                  }}
                >
                  Salva
                </Button>
              </DialogActions>
            </Dialog>
          </Box>

          {/* ========== CENTER COLUMN: AI Reply ========== */}
          <Box sx={{ flex: 1, minWidth: 0, p: 2, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
              Bozza Risposta AI
            </Typography>

            {/* Reply-to and CC fields (always editable) */}
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                label="A (destinatario)"
                value={replyToAddress}
                onChange={(e) => setReplyToAddress(e.target.value)}
                size="small"
                InputProps={{ sx: { fontSize: '0.82rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.82rem' } }}
              />
              <TextField
                fullWidth
                label="CC"
                value={ccAddresses}
                onChange={(e) => setCcAddresses(e.target.value)}
                size="small"
                placeholder="email1@esempio.it, email2@esempio.it"
                InputProps={{ sx: { fontSize: '0.82rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.82rem' } }}
              />
            </Stack>

            {editMode ? (
              <Box>
                <TextField
                  fullWidth
                  label="Oggetto"
                  value={editedSubject}
                  onChange={(e) => setEditedSubject(e.target.value)}
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={8}
                  maxRows={20}
                  value={editedDraft}
                  onChange={(e) => setEditedDraft(e.target.value)}
                  variant="outlined"
                  placeholder="Scrivi la risposta..."
                  helperText="Modifica solo il corpo della mail. Le annotazioni AI sono mostrate separatamente sotto."
                />
              </Box>
            ) : (
              <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Oggetto:</strong> {editedSubject || action.reply_subject}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="body1"
                  component="div"
                  sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                >
                  {highlightCrmText(editedDraft)}
                </Typography>
                {crmKeywords.length > 0 && (
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 14, height: 14, bgcolor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 0.5, display: 'inline-block' }} />
                    <Typography variant="caption" color="text.secondary">
                      = dato integrato dal CRM
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Operator notes box - separate from email body */}
            {operatorNotes && (
              <Alert
                severity="info"
                variant="outlined"
                icon={<NoteIcon fontSize="small" />}
                sx={{
                  mb: 2,
                  bgcolor: '#fff8e1',
                  borderColor: '#ffb74d',
                  '& .MuiAlert-icon': { color: '#f57c00' },
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#e65100', display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  📝 Note AI per l'operatore (non incluse nella mail)
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: 'text.secondary', fontSize: '0.82rem' }}>
                  {operatorNotes}
                </Typography>
              </Alert>
            )}

            {/* Attached files display + upload button */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  📎 Allegati ({attachments.length})
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.2 }}
                >
                  Allega file
                </Button>
              </Stack>
              {attachments.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {attachments.map((att, idx) => (
                    <Chip
                      key={idx}
                      icon={att.type === 'invoice' ? <InvoiceIcon fontSize="small" /> : att.type === 'file' ? <FileIcon fontSize="small" /> : <AccountStatementIcon fontSize="small" />}
                      label={att.label}
                      size="small"
                      color={att.type === 'invoice' ? 'primary' : att.type === 'file' ? 'default' : 'secondary'}
                      variant="outlined"
                      clickable
                      onClick={() => {
                        if (att.type === 'invoice' && att.id) {
                          openAuthenticatedUrl(`/invoices/${att.id}/pdf`);
                        } else if (att.type === 'account_statement' && att.id) {
                          const path = att.fromInvoiceId
                            ? `/customers/${att.id}/account-statement/pdf?fromInvoiceId=${att.fromInvoiceId}`
                            : `/customers/${att.id}/account-statement/pdf`;
                          openAuthenticatedUrl(path);
                        } else if (att.type === 'file' && att.content) {
                          const byteChars = atob(att.content);
                          const byteArray = new Uint8Array(byteChars.length);
                          for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
                          const blob = new Blob([byteArray], { type: att.contentType || 'application/octet-stream' });
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank');
                        }
                      }}
                      onDelete={() => removeAttachment(idx)}
                      sx={{ fontSize: '0.72rem', cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Regenerate with custom instructions */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Rigenera risposta con istruzioni
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={1}
                maxRows={3}
                placeholder="Es: Rispondi in modo più formale, proponi un appuntamento..."
                value={regenPrompt}
                onChange={(e) => setRegenPrompt(e.target.value)}
                disabled={regenerating}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleRegenerate}
                disabled={regenerating || !regenPrompt.trim()}
                startIcon={regenerating ? <CircularProgress size={16} /> : <RegenIcon />}
                sx={{ minWidth: 120, whiteSpace: 'nowrap' }}
              >
                {regenerating ? 'Rigenero...' : 'Rigenera'}
              </Button>
            </Stack>
          </Box>

          {/* ========== RIGHT COLUMN: CRM Panel ========== */}
          <Box sx={{
            flex: '0 0 28%', minWidth: 0, overflow: 'auto',
            borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' },
            bgcolor: '#f8f9fc',
          }}>
            {loadingCrm && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Caricamento dati CRM...
                </Typography>
              </Box>
            )}

            {!loadingCrm && hasCrmPanel && (
              <Box sx={{ p: 2 }}>
                {/* CRM Header */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <CustomerIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                    Dati CRM
                  </Typography>
                  {customer && (
                    <Tooltip title="Apri scheda cliente">
                      <IconButton size="small" onClick={() => window.open(`/crm/clienti/${customer.id}`, '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>

                {/* CRM Sections */}
                {crmSections.length > 0 && crmSections.map((section, idx) => {
                  const isFattureSection = section.title && section.title.toUpperCase().includes('FATTUR');
                  return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      mb: 1.5, p: 1.5, borderRadius: 2,
                      bgcolor: section.isWarning ? 'warning.50' : 'background.paper',
                      border: '1px solid',
                      borderColor: section.isWarning ? 'warning.light' : 'divider',
                    }}
                  >
                    {section.isWarning ? (
                      <Alert severity="warning" sx={{ py: 0, px: 0, bgcolor: 'transparent', '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
                        {section.title}
                      </Alert>
                    ) : (
                      <>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                          {sectionIcon(section.title)}
                          <Typography variant="caption" sx={{
                            fontWeight: 700, color: 'primary.main', textTransform: 'uppercase',
                            fontSize: '0.68rem', letterSpacing: '0.5px',
                          }}>
                            {section.title}
                          </Typography>
                        </Stack>
                        {section.items.map((item, i) => {
                          const invoiceMatch = isFattureSection && invoicesData[i];
                          const isAttached = invoiceMatch && attachments.some(a => a.type === 'invoice' && a.id === invoiceMatch.id);
                          return (
                            <Stack key={i} direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" sx={{
                                fontSize: '0.76rem', lineHeight: 1.5, pl: 1, py: 0.2,
                                color: 'text.secondary',
                                borderLeft: '2px solid', borderColor: 'primary.light', ml: 0.5,
                                flex: 1,
                              }}>
                                {item}
                              </Typography>
                              {invoiceMatch && (
                                <Tooltip title={isAttached ? 'Rimuovi allegato' : 'Allega fattura PDF'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleInvoiceAttachment(invoiceMatch)}
                                    sx={{
                                      p: 0.3,
                                      color: isAttached ? 'primary.main' : 'text.disabled',
                                      bgcolor: isAttached ? 'primary.50' : 'transparent',
                                      '&:hover': { color: 'primary.main' },
                                    }}
                                  >
                                    <AttachFileIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          );
                        })}
                        {/* Allega Estratto Conto button after FATTURE section */}
                        {isFattureSection && invoicesData.length > 0 && (
                          <Button
                            size="small"
                            variant={attachments.some(a => a.type === 'account_statement') ? 'contained' : 'outlined'}
                            color="secondary"
                            startIcon={<AccountStatementIcon sx={{ fontSize: 14 }} />}
                            onClick={toggleAccountStatement}
                            sx={{ mt: 1, textTransform: 'none', fontSize: '0.7rem', py: 0.3 }}
                          >
                            {attachments.some(a => a.type === 'account_statement') ? 'Estratto Conto allegato ✓' : 'Allega Estratto Conto'}
                          </Button>
                        )}
                      </>
                    )}
                  </Paper>
                  );
                })}

                {/* Quick CRM Actions */}
                {crmActions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <QuickActionIcon color="secondary" fontSize="small" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                        Azioni rapide suggerite
                      </Typography>
                    </Stack>
                    <Stack spacing={1}>
                      {crmActions.map((crmAction, idx) => {
                        const customerId = customer?.id || email.customer_id;
                        const actionUrl = customerId
                          ? (crmAction.action === 'aggiorna_anagrafica'
                            ? `/crm/clienti/${customerId}/modifica`
                            : `/crm/clienti/${customerId}`)
                          : null;
                        const priorityColor = crmAction.priority === 'high' ? 'error' : crmAction.priority === 'medium' ? 'warning' : 'default';

                        return (
                          <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                              p: 1.5, borderRadius: 2,
                              bgcolor: crmAction.priority === 'high' ? '#fff5f5' : 'background.paper',
                              border: '1px solid', borderColor: crmAction.priority === 'high' ? 'error.light' : 'divider',
                              cursor: actionUrl ? 'pointer' : 'default',
                              '&:hover': actionUrl ? { bgcolor: 'action.hover', borderColor: 'primary.light' } : {},
                            }}
                            onClick={actionUrl ? () => window.open(actionUrl, '_blank') : undefined}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                              {crmActionIcons[crmAction.action] || <QuickActionIcon fontSize="small" color="action" />}
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', flex: 1, wordBreak: 'break-word' }}>
                                {crmAction.label || crmAction.action}
                              </Typography>
                              <Chip
                                label={crmAction.priority || 'medium'}
                                size="small"
                                color={priorityColor}
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 18, flexShrink: 0 }}
                              />
                            </Stack>
                            {crmAction.reason && (
                              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', display: 'block', pl: 3 }}>
                                {crmAction.reason}
                              </Typography>
                            )}
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {/* Raw CRM context fallback */}
                {crmSections.length === 0 && crmContext && (
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', fontSize: '0.68rem' }}>
                      Contesto CRM
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.76rem', whiteSpace: 'pre-wrap', color: 'text.secondary', mt: 0.5 }}>
                      {crmContext}
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {/* No CRM data at all */}
            {!loadingCrm && !hasCrmPanel && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CustomerIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Nessun dato CRM disponibile
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Cliente non riconosciuto
                </Typography>
              </Box>
            )}
          </Box>

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="text"
          color="inherit"
        >
          Chiudi
        </Button>

        <Button
          onClick={handleReject}
          color="error"
          variant="outlined"
          startIcon={<CloseIcon />}
        >
          Rifiuta
        </Button>

        <Box sx={{ flex: 1 }} />

        {editMode ? (
          <Button
            onClick={handleSaveDraft}
            disabled={saving}
            variant="outlined"
            startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
          >
            Salva Modifiche
          </Button>
        ) : (
          <Button
            onClick={() => setEditMode(true)}
            variant="outlined"
            startIcon={<EditIcon />}
          >
            Modifica
          </Button>
        )}

        <Button
          onClick={handleApprove}
          disabled={sending}
          variant="contained"
          color="primary"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          {sending ? 'Invio...' : 'Approva e Invia'}
        </Button>
      </DialogActions>

      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        multiple
        onChange={handleFileUpload}
      />

      {/* Account Statement sub-dialog */}
      <Dialog
        open={accountStatementOpen}
        onClose={() => setAccountStatementOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccountStatementIcon color="secondary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Seleziona fattura iniziale</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            L'estratto conto includerà tutte le fatture a partire da quella selezionata.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {accountStatementLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Caricamento fatture...</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Nr. Fattura</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }} align="right">Importo</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Stato</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* "All invoices" option */}
                  <TableRow
                    hover
                    selected={selectedFromInvoiceId === null}
                    onClick={() => setSelectedFromInvoiceId(null)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Radio checked={selectedFromInvoiceId === null} size="small" />
                    </TableCell>
                    <TableCell colSpan={4} sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                      Tutte le fatture
                    </TableCell>
                  </TableRow>
                  {accountStatementInvoices.map((inv) => (
                    <TableRow
                      key={inv.id}
                      hover
                      selected={selectedFromInvoiceId === inv.id}
                      onClick={() => setSelectedFromInvoiceId(inv.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Radio checked={selectedFromInvoiceId === inv.id} size="small" />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{inv.nuf || inv.invoice_number || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {(inv.f_emission || inv.issue_date) ? new Date(inv.f_emission || inv.issue_date).toLocaleDateString('it-IT') : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }} align="right">
                        {(inv.f_totale != null || inv.total_amount != null) ? `€ ${Number(inv.f_totale ?? inv.total_amount).toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {(inv.status === 'paid' || inv.stato === 'pagata') ? '✅ Pagata'
                          : (inv.status === 'partial') ? '🔶 Parziale'
                          : (inv.balance > 0 || inv.saldo_residuo > 0) ? '⚠️ Da pagare'
                          : inv.status || inv.stato || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setAccountStatementOpen(false)} color="inherit">Annulla</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={confirmAccountStatement}
            disabled={accountStatementLoading}
            startIcon={<AccountStatementIcon />}
          >
            Allega estratto conto
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
